import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { processingGuidance } from '../utils/processingGuidance';
import useUploadDialog from '../hooks/useUploadDialog';
import { normalizeProcessingError } from '../utils/processingState';

export default function PhotoProcessingDetails({ image, onClose, onCheck, onRemove }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const dialogRef = useUploadDialog(true, onClose);
  const error = normalizeProcessingError(image.processing_error);
  const status = image.processing_status || 'unknown';
  const ready = status === 'ready';
  const guidance = processingGuidance(status, error);
  async function check() {
    setBusy(true); setNotice('');
    try { await onCheck(image.id); setNotice('Status checked. See the latest status above.'); }
    catch { setNotice('We could not check the status. Check your connection and try again. Do not upload another copy yet.'); }
    finally { setBusy(false); }
  }
  return createPortal(
    <div className="photo-status-backdrop" onClick={onClose}>
      <section ref={dialogRef} className="photo-status-dialog" role="dialog" aria-modal="true" aria-labelledby="photo-status-title" aria-describedby="photo-status-description" tabIndex={-1} onClick={event => event.stopPropagation()}>
        <header><h2 id="photo-status-title">Photo processing details</h2><button type="button" aria-label="Close photo details" onClick={onClose}>×</button></header>
        <p className="photo-status-filename">{image.original_filename || image.title || image.filename || 'Photo'}</p>
        <h3>{guidance.title}</h3>
        <p id="photo-status-description">{guidance.description}</p>
        {error?.message && <div className="photo-status-error"><strong>Recorded error</strong><p>{error.message}</p></div>}
        {guidance.next && <p>{guidance.next}</p>}
        {!ready && <p className="photo-status-reference">Preview status does not verify original-file storage. Checking status does not upload or replace your file.</p>}
        <p className="photo-status-reference">Photo reference: {image.id}</p>
        <p role="status" aria-live="polite">{busy ? 'Checking status…' : notice}</p>
        <div className="photo-status-actions">
          <button type="button" disabled={busy} onClick={check}>{busy ? 'Checking…' : 'Check status'}</button>
          <button type="button" onClick={onClose}>Close</button>
        </div>
        <div className="photo-status-remove">
          <p>Removing this photo deletes its library record, original and generated files. This cannot be undone.</p>
          <button type="button" disabled={busy} onClick={async () => { if (await onRemove(image.id) !== false) onClose(); }}>Remove photo…</button>
        </div>
      </section>
    </div>, document.body
  );
}
